import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Room } from 'src/app/model/room/room';
import { RoomModel } from 'src/app/model/room/room.model';
import { doc, DocumentData, DocumentSnapshot, getFirestore, onSnapshot } from 'firebase/firestore';

@Injectable({
  providedIn: 'root',
})
export class RoomService implements OnDestroy {
  private injector = inject(Injector);
  private roomObservables: Map<string, Observable<Room | null>> = new Map();
  private unsubscribeFunctions: Map<string, () => void> = new Map();

  ngOnDestroy(): void {
    this.unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeFunctions.clear();
    this.roomObservables.clear();
  }

  getObservable(roomId: string, userId: string): Observable<Room | null> {
    const roomPath = RoomService.roomPath(roomId, userId);
    if (this.roomObservables.has(roomPath)) {
      return this.roomObservables.get(roomPath) ?? new Observable<null>();
    } else {
      const observable = new Observable<Room | null>((subscriber) => {
        const unsubscribe = this.addDocListener(roomPath, (doc) => {
          if (doc?.exists()) {
            subscriber.next(new Room(doc.data() as RoomModel, roomId, roomPath));
          } else {
            subscriber.next(null);
          }
        });

        this.unsubscribeFunctions.set(roomPath, unsubscribe);

        return () => {
          unsubscribe();
          this.unsubscribeFunctions.delete(roomPath);
        };
      });

      this.roomObservables.set(roomPath, observable);
      return observable;
    }
  }

  getSignal(roomId: string, userId: string) {
    const observable = this.getObservable(roomId, userId);
    return toSignal(observable, { injector: this.injector });
  }

  static roomPath(roomId: string, userId: string): string {
    return `matches/${userId}/rooms/${roomId}`;
  }

  private addDocListener(
    reference: string,
    listener: (doc: DocumentSnapshot<DocumentData> | null) => void,
  ): () => void {
    // Convert path string to DocumentReference
    const db = getFirestore();
    const docRef = doc(db, reference);

    // Return the unsubscribe function directly
    return onSnapshot(
      docRef,
      (snapshot) => {
        listener(snapshot);
      },
      (error) => {
        console.error(error);
      },
    );
  }
}
